import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle2, Loader2, XCircle, ArrowRight, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;
  const pollInterval = 2000;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus('error');
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscriptions/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to update role
        if (refreshUser) {
          await refreshUser();
        }
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPollCount(prev => prev + 1);
      if (pollCount + 1 >= maxPolls) {
        setStatus('error');
      } else {
        setTimeout(pollPaymentStatus, pollInterval);
      }
    }
  };

  const handleRetry = () => {
    setPollCount(0);
    setStatus('checking');
    pollPaymentStatus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 flex items-center justify-center px-4 pt-20">
      <Card className="max-w-md w-full bg-muted/50 border-border backdrop-blur-sm">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-indigo-400 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Processing Your Subscription
              </h2>
              <p className="text-muted-foreground mb-6">
                Please wait while we confirm your payment...
              </p>
              <div className="flex justify-center gap-1">
                {[...Array(maxPolls)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`w-2 h-2 rounded-full ${
                      i <= pollCount ? 'bg-indigo-500' : 'bg-accent'
                    }`} 
                  />
                ))}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" data-testid="success-title">
                Welcome to B2B!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. You have access to all B2B features including the job board and wholesale marketplace.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-border text-muted-foreground hover:bg-accent"
                  onClick={() => navigate('/staffing')}
                >
                  View Job Board
                </Button>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Payment Failed
              </h2>
              <p className="text-muted-foreground mb-6">
                Your payment could not be processed. Please try again or contact support.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                  onClick={() => navigate('/b2b/pricing')}
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-border text-muted-foreground hover:bg-accent"
                  onClick={() => window.location.href = 'mailto:hello@eventism.com'}
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <RefreshCw className="h-10 w-10 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Unable to Verify Payment
              </h2>
              <p className="text-muted-foreground mb-6">
                We couldn't verify your payment status. This might take a moment. Please try refreshing or contact support if the issue persists.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                  onClick={handleRetry}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry Verification
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-border text-muted-foreground hover:bg-accent"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
